import { FC, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/layout';
import {
  Accordion,
  Button,
  Flex,
  IconButton,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack,
  HStack
} from '@chakra-ui/react';
import { Crucible } from 'types';
import { HiOutlineRefresh } from 'react-icons/hi';
import { useRewardPrograms } from 'store/zustand/useRewardPrograms';
import { useSubscriptions } from 'hooks/query/useSubscriptions';
import { LeaderboardTable } from 'components/rewardPrograms/LeaderboardTable';
import { useRouteMatch } from 'react-router-dom';
import RewardProgramSelector from './RewardProgramSelector';
import RewardProgramInformation from './RewardProgramInformation';
import RewardProgramActions from './RewardProgramActions';
import Subscription from './Subscription';
import RewardsSummary from './RewardsSummary';
import RewardProgramDescription from './RewardProgramDescription';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ModalType } from 'components/modals/types';
import { useModal } from 'store/modals';
import { useRewardProgramState } from 'hooks/query/useRewardProgramState';
import { useTransactions } from 'store/transactions/useTransactions';
import { TxnStatus, TxnType } from 'store/transactions/types';
import { PowerSwitchStatus } from 'api/blockchain/rewardProgram';
import { CrucibleStats } from './CrucibleStats';

dayjs.extend(relativeTime);

type Props = {
  crucible: Crucible;
  setTabIndex: (index: number) => void;
  setTransferTabIndex: (index: number) => void;
};

const tabProps = {
  borderRadius: 'lg',
  fontSize: ['14px', '16px'],
  fontWeight: '500',
  _selected: { color: 'black', bg: 'cyan.500' },
  marginBottom: '1px'
};

const Rewards: FC<Props> = ({ crucible, setTabIndex, setTransferTabIndex }) => {
  const [secondaryTabIndex, setSecondaryTabIndex] = useState(0);
  const { currentRewardProgram } = useRewardPrograms();
  const {
    data: subscriptionsData,
    dataUpdatedAt,
    refetch
  } = useSubscriptions();
  const { openModal } = useModal();
  const { transactions } = useTransactions();
  const { stakingToken, rewardToken, bonusTokens } =
    subscriptionsData?.subscriptionsMeta || {};
  const { data: rewardProgramState } =
    useRewardProgramState(currentRewardProgram);

  const match = useRouteMatch(['/explore/:crucibleId/:tab?']) as any;
  const isReadonly = !!match?.isExact;

  const isExiting = useMemo(() => {
    return !!transactions.find(
      (txn) =>
        txn.type === TxnType.rageQuit &&
        txn.status === TxnStatus.PendingOnChain &&
        txn.crucibleAddress === crucible.id
    );
  }, [transactions, crucible?.id]);

  return (
    <VStack alignItems='stretch' spacing={5}>
      <RewardProgramSelector
        crucible={isReadonly ? crucible : undefined}
        isReadOnly={isReadonly}
      />
      {!subscriptionsData?.subscriptions ||
      !subscriptionsData?.subscriptionsMeta ||
      !subscriptionsData?.subscriptionsSummary ? (
        <Box p={[4, 8]} borderRadius='xl'>
          <Spinner />
        </Box>
      ) : (
        <>
          <RewardProgramInformation
            rewardProgramAddress={currentRewardProgram.address}
            rewardSchedules={
              subscriptionsData?.subscriptionsMeta.rewardProgramData
                .rewardSchedules
            }
          />
          <CrucibleStats
            crucible={crucible}
            pathMatch={match}
            rewardProgramAddress={currentRewardProgram.address}
          />
          {!isReadonly && (
            <RewardProgramActions
              crucible={crucible}
              stakingToken={subscriptionsData.subscriptionsMeta.stakingToken}
              setTabIndex={setTabIndex}
              setTransferTabIndex={setTransferTabIndex}
            />
          )}
          {subscriptionsData.subscriptions &&
            subscriptionsData.subscriptions.length > 0 && (
              <>
                <Box my={4}>
                  <Flex justifyContent='space-between'>
                    <Box textAlign='left'>
                      <Text fontSize='lg' fontWeight='600'>
                        Subscriptions
                      </Text>
                      <Text color='gray.300' fontSize='sm'>
                        Last updated:{' '}
                        {dayjs(dayjs.unix(dataUpdatedAt / 1000)).fromNow()}
                      </Text>
                    </Box>
                    <HStack spacing={2}>
                      {rewardProgramState !== PowerSwitchStatus.OFFLINE &&
                        rewardProgramState !== PowerSwitchStatus.SHUTDOWN &&
                        !isReadonly && (
                          <Button
                            variant='outline'
                            colorScheme='cyan'
                            disabled={isExiting}
                            onClick={() =>
                              openModal(ModalType.rageQuit, {
                                type: rewardProgramState,
                                crucibleId: crucible.id,
                                currentRewardProgram,
                                stakingTokenAddress: stakingToken?.address ?? ''
                              })
                            }
                          >
                            Emergency Exit
                          </Button>
                        )}
                      <IconButton
                        bg='gray.700'
                        variant='ghost'
                        fontSize='xl'
                        icon={<HiOutlineRefresh />}
                        aria-label='refresh'
                        onClick={() => refetch()}
                      />
                    </HStack>
                  </Flex>
                </Box>
                <RewardsSummary />
                <Accordion allowMultiple>
                  {subscriptionsData.subscriptions.map((subscription, idx) => (
                    <Subscription
                      key={idx}
                      crucible={crucible}
                      subscription={subscription}
                      isReadonly={isReadonly}
                      rewardProgramState={rewardProgramState}
                      currentRewardProgram={currentRewardProgram}
                    />
                  ))}
                </Accordion>
              </>
            )}
          <Tabs
            index={secondaryTabIndex}
            isFitted
            defaultIndex={0}
            onChange={(index) => {
              setSecondaryTabIndex(index);
            }}
            mb={6}
            size='md'
          >
            <TabList
              fontWeight='600'
              borderRadius='xl'
              my={2}
              border='1px solid'
              padding='5px'
            >
              <Tab {...tabProps}>Description</Tab>
              <Tab {...tabProps}>Leaderboard</Tab>
            </TabList>
          </Tabs>
          {/* Workaround for layout issues with Table causing overflow of container in the x-axis */}
          <Box
            sx={{
              position: 'relative',
              paddingBottom: secondaryTabIndex === 0 ? 0 : 7440
            }}
          >
            {secondaryTabIndex === 0 && (
              <Box>
                {!!stakingToken && !!rewardToken && !!bonusTokens ? (
                  <RewardProgramDescription
                    rewardProgram={currentRewardProgram}
                    rewardProgramTokens={{
                      stakingToken,
                      rewardToken,
                      bonusTokens
                    }}
                  />
                ) : (
                  <Box p={[4, 8]} borderRadius='xl'>
                    <Spinner />
                  </Box>
                )}
              </Box>
            )}
            {secondaryTabIndex === 1 && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0 }}>
                <LeaderboardTable
                  embedded
                  hideHeading
                  hideProgramSelector
                  rewardProgramAddress={currentRewardProgram.address}
                  selectedCrucible={crucible}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </VStack>
  );
};

export default Rewards;
